import { useEffect } from "react";
import { getRotatingSocialImage } from "@/lib/socialShareImages";

interface MetaTagsOptions {
  title?: string;
  description?: string;
  url?: string;
  image?: string;
  siteName?: string;
  /**
   * Canonical URL for this page. If omitted, derived from window.location.origin
   * + pathname (query/hash stripped, trailing slash removed except root).
   * Pass an explicit value for routes where the query IS the canonical (e.g. /search?q=...).
   */
  canonical?: string;
}

/**
 * Strip query params + hash and remove trailing slash (except root "/").
 */
function normalizeCanonical(rawUrl: string): string {
  try {
    const u = new URL(rawUrl);
    let path = u.pathname.replace(/\/+$/, "");
    if (path === "") path = "/";
    return `${u.origin}${path}`;
  } catch {
    return rawUrl;
  }
}

export function useMetaTags({ title, description, url, image, siteName = "AskThePods", canonical }: MetaTagsOptions) {
  useEffect(() => {
    const prev = document.title;
    if (title) document.title = title;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const canonicalHref = canonical
      ? normalizeCanonical(canonical)
      : typeof window !== "undefined"
        ? normalizeCanonical(window.location.href)
        : "";
    const ogUrl = url || canonicalHref || (typeof window !== "undefined" ? window.location.href : "");
    const ogImage = image || (ogUrl ? getRotatingSocialImage(ogUrl) : undefined);

    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (title) {
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (ogUrl) setMeta("property", "og:url", ogUrl);
    if (ogImage) {
      setMeta("property", "og:image", ogImage);
      setMeta("name", "twitter:image", ogImage);
    }
    if (siteName) setMeta("property", "og:site_name", siteName);
    setMeta("property", "og:type", "website");
    setMeta("name", "twitter:card", "summary_large_image");

    if (canonicalHref) setLink("canonical", canonicalHref);

    return () => {
      document.title = prev;
    };
  }, [title, description, url, image, siteName, canonical]);
}

